import "./style.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomFullNamePopUp from "@/src/components/CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import CustomDocumentPreparationPopUp, {
  DocumentPreparationSubmissionMode,
} from "@/src/components/CustomUtils/CustomDocumentPreparationPopUp/CustomDocumentPreparationPopUp";
import TasksHeader from "./TasksHeader/TasksHeader";
import TasksBody from "./TasksBody/TasksBody";
import TasksFooter from "./TasksFooter/TasksFooter";
import { RootState } from "@/src/store";
import { IFiltersList, IRowData } from "@/src/types/applicationOnline";
import { getTaskListItemId, ITasksTablePayload, ITasksTableRow, mapTaskListItemToTableRow } from "@/src/types/tasksTable";
import ApplyOnline from "../ApplyOnline/ApplyOnline";
import { ITaskListItem, ITasksListResponse, tasksPageRequests } from "@/src/api/tasksPageRequests";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { setData } from "@/src/store/slices/TasksPage/tasksTableSlice";
import { useFilteredObject } from "@/src/hooks/useFilteredObject";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { buildTasksFiltersList } from "@/src/utils/tasks/buildTasksFiltersList";
import { mapTaskUiStatusFilterToApi } from "@/src/utils/tasks/mapTaskStatus";

type RawFiltersPayload = Record<string, string | null | Array<string | boolean | number>>;

const normalizeTasksResponse = (
  payload: ITasksListResponse | ITaskListItem[] | null | undefined,
): {
  data: ITasksTableRow[];
  count_form: number;
  taskIdMap: Record<number, string>;
  taskRowMap: Record<number, ITaskListItem>;
} => {
  if (!payload) return { data: [], count_form: 0, taskIdMap: {}, taskRowMap: {} };

  let rows: ITaskListItem[] = [];
  let countForm: number | undefined;

  if (Array.isArray(payload)) {
    rows = payload;
    countForm = payload.length;
  } else {
    rows = Array.isArray(payload.data) ? payload.data : [];
    countForm = payload.count ?? (payload as { count_task?: number }).count_task;
  }

  const taskIdMap: Record<number, string> = {};
  const taskRowMap: Record<number, ITaskListItem> = {};
  const normalizedRows: ITasksTableRow[] = rows.map((item) => {
    const row = mapTaskListItemToTableRow(item);
    taskIdMap[row.task_id] = getTaskListItemId(item);
    taskRowMap[row.task_id] = item;
    return row;
  });

  return {
    data: normalizedRows,
    count_form: Number.isFinite(countForm) ? Number(countForm) : normalizedRows.length,
    taskIdMap,
    taskRowMap,
  };
};

const normalizeTasksFiltersPayload = (filters: RawFiltersPayload): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  const addArray = (key: string, value: unknown) => {
    if (Array.isArray(value) && value.length > 0) normalized[key] = value;
  };
  const addValue = (key: string, value: unknown) => {
    if (typeof value === "string" && value.trim() !== "") normalized[key] = value.trim();
  };

  addValue("query", filters.query);
  addArray("assignee_id", filters.assignee);
  addArray("executor_id", filters.executor_id);
  addArray("type", filters.type);
  addArray("intake", filters.intake);
  addArray("admission_semester", filters.admission_semester);
  addArray("form_of_education", filters.form_of_education);
  addArray("course", filters.course);
  addArray("category", filters.country ?? filters.category);
  addArray("medium_of_instruction", filters.medium_of_instruction);
  addArray("duration", filters.duration);
  addArray("transfer_fee", filters.transfer_fee);
  addArray("priority", filters.priority);
  if (Array.isArray(filters.status) && filters.status.length > 0) {
    const mappedStatus = Array.from(
      new Set(filters.status.map((value) => mapTaskUiStatusFilterToApi(String(value))).filter((value) => value !== "")),
    );
    if (mappedStatus.length > 0) normalized.status = mappedStatus;
  }
  addArray("deadline_date", filters.deadline_date);

  return normalized;
};

const TasksLayout = () => {
  const dispatch = useDispatch();
  const actualOfficeTable = useSelector((state: RootState) => state.tasksFilters.actualOfficeTable);
  const bodyFilters = useSelector((state: RootState) => state.tasksFilters.filterResults);
  const actualFilterStatus = useSelector((state: RootState) => state.tasksFilters.filterStatus);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [isShowSettingsModal, setIsShowSettingsModal] = useState(false);
  const [isApPopUpFullName, setIsApPopUpFullName] = useState<boolean>(false);
  const [isDocumentPreparationPopUpOpen, setIsDocumentPreparationPopUpOpen] = useState<boolean>(false);
  const [documentPrepSubmissionMode, setDocumentPrepSubmissionMode] = useState<DocumentPreparationSubmissionMode>("update");
  const [currentDataPopUp, setCurrentDataPopUp] = useState<IRowData["userInfo"] | undefined>();
  const [currentStudentId, setCurrentStudentId] = useState<number | undefined>();
  const [currentApplyInfoId, setCurrentApplyInfoId] = useState<number | undefined>();
  const [isCurrentPage, setIsCurrentPage] = useState<string>("1");
  const [isSelectPagination, setIsSelectPagination] = useState<string>("5");
  const [tableFiltersList, setTableFiltersList] = useState<IFiltersList | undefined>();
  const [actualQuantityRows, setActualQuantityRows] = useState<number>(0);
  const [taskIdMap, setTaskIdMap] = useState<Record<number, string>>({});
  const [taskRowMap, setTaskRowMap] = useState<Record<number, ITaskListItem>>({});

  const bodyFilterFetch = useFilteredObject(bodyFilters);
  const formRef = useRef<HTMLDivElement>(null);

  const updateShowFormClass = useCallback(() => {
    if (formRef.current) {
      const value = localStorage.getItem("tasksPageCreate");
      if (value === "show") formRef.current.classList.add("show");
      else formRef.current.classList.remove("show");
    }
  }, []);

  const closeApplyOnlineForm = useCallback(() => {
    localStorage.removeItem("tasksPageCreate");
    window.dispatchEvent(new Event("tasksPageCreateChanged"));
  }, []);

  const patchTaskRowMap = useCallback((taskRowId: number, patch: Partial<ITaskListItem>) => {
    setTaskRowMap((prev) => {
      const existing = prev[taskRowId];
      if (!existing) return prev;
      return { ...prev, [taskRowId]: { ...existing, ...patch } };
    });
  }, []);

  const loadActualForm = useCallback(async () => {
    dispatch(setIsPreloyder(true));
    try {
      const offsetTableRow = Number(isSelectPagination) * (Number(isCurrentPage) - 1);
      const requestFilters = normalizeTasksFiltersPayload(bodyFilterFetch as RawFiltersPayload);
      const hasBodyFilters = Object.keys(requestFilters).length > 0;
      const shouldUseFiltersApply = actualFilterStatus !== "All" || hasBodyFilters || actualOfficeTable !== "ALL";

      const dataTable = shouldUseFiltersApply
        ? await tasksPageRequests.applyFilters(isSelectPagination, offsetTableRow, actualOfficeTable, requestFilters)
        : await tasksPageRequests.getList(isSelectPagination, offsetTableRow);

      if (dataTable) {
        const normalizedTable = normalizeTasksResponse(dataTable);
        const filtersPayload = buildTasksFiltersList(normalizedTable.data);
        const tablePayload: ITasksTablePayload = {
          data: normalizedTable.data,
          count_form: normalizedTable.count_form,
          filters_list: filtersPayload,
        };
        console.log("[TasksPage] API raw:", dataTable);
        console.log("[TasksPage] Table payload (Redux):", tablePayload);
        dispatch(setData(tablePayload));
        setTableFiltersList(filtersPayload);
        setActualQuantityRows(normalizedTable.count_form);
        setTaskIdMap(normalizedTable.taskIdMap);
        setTaskRowMap(normalizedTable.taskRowMap);
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
      console.error(error);
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [dispatch, isSelectPagination, isCurrentPage, bodyFilterFetch, actualOfficeTable, actualFilterStatus]);

  useEffect(() => {
    loadActualForm();
    window.addEventListener("tasksPageCreateChanged", updateShowFormClass);
    return () => {
      window.removeEventListener("tasksPageCreateChanged", updateShowFormClass);
    };
  }, [loadActualForm, updateShowFormClass]);

  useEffect(() => {
    setIsCurrentPage("1");
  }, [actualOfficeTable, actualFilterStatus]);

  const refreshCurrentStudentUserInfo = useCallback(async (): Promise<IRowData["userInfo"] | undefined> => {
    if (currentStudentId == null) return undefined;
    try {
      const userInfo = await fetchApplicantUserInfo(currentStudentId);
      if (userInfo) setCurrentDataPopUp(userInfo);
      return userInfo;
    } catch (error) {
      console.log("refreshCurrentStudentUserInfo error:", error);
      return undefined;
    }
  }, [currentStudentId]);

  return (
    <>
      <div ref={formRef} className="tasks-page-form">
        <ApplyOnline loadActualForm={loadActualForm} onCloseForm={closeApplyOnlineForm} />
      </div>

      <div className="container tasks-page">
        <CustomFullNamePopUp
          isApPopUpFullName={isApPopUpFullName}
          setIsApPopUpFullName={setIsApPopUpFullName}
          setIsDocumentPreparationPopUpOpen={setIsDocumentPreparationPopUpOpen}
          setDocumentPrepSubmissionMode={setDocumentPrepSubmissionMode}
          currentDataPopUp={currentDataPopUp}
          currentStudentId={currentStudentId}
          currentApplyInfoId={currentApplyInfoId}
          tableContext="tasks"
          onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
          onRefreshTableData={loadActualForm}
        />
        <CustomDocumentPreparationPopUp
          isOpen={isDocumentPreparationPopUpOpen}
          setIsOpen={setIsDocumentPreparationPopUpOpen}
          tableContext="tasks"
          submissionMode={documentPrepSubmissionMode}
          taskIdMap={taskIdMap}
          onSendSuccess={loadActualForm}
          onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
        />
        <TasksHeader
          isShowSettingsModal={isShowSettingsModal}
          setIsShowSettingsModal={setIsShowSettingsModal}
          setIsDocumentPreparationPopUpOpen={setIsDocumentPreparationPopUpOpen}
          setDocumentPrepSubmissionMode={setDocumentPrepSubmissionMode}
          loadActualForm={loadActualForm}
          taskIdMap={taskIdMap}
          taskRowMap={taskRowMap}
          onTaskUpdated={patchTaskRowMap}
        />
        <TasksBody
          headerCheckboxRef={headerCheckboxRef}
          setIsApPopUpFullName={setIsApPopUpFullName}
          setCurrentDataPopUp={setCurrentDataPopUp}
          setCurrentStudentId={setCurrentStudentId}
          setCurrentApplyInfoId={setCurrentApplyInfoId}
          tableFiltersList={tableFiltersList}
          isShowSettingsModal={isShowSettingsModal}
          taskIdMap={taskIdMap}
          onTaskUpdated={patchTaskRowMap}
        />
        <TasksFooter
          isCurrentPage={isCurrentPage}
          setIsCurrentPage={setIsCurrentPage}
          isSelectPagination={isSelectPagination}
          setIsSelectPagination={setIsSelectPagination}
          actualQuantityRows={actualQuantityRows}
        />
      </div>
    </>
  );
};

export default TasksLayout;
